/**
 * FILE: src/hooks/useNotificationTemplatesApi.js
 * 
 * API hook for notification template management
 * 
 * Features:
 * - CRUD operations for notification templates
 * - Template preview with variable substitution
 * - Test sending
 * - Metrics retrieval
 * - Metadata (categories, channels, priorities)
 */

import { useCallback, useRef, useEffect } from "react";
import useApi from "./useApi";
import { endpoints } from "config/api";

/**
 * Hook for notification template API operations
 */
export default function useNotificationTemplatesApi() {
  const { request, loading, error } = useApi();
  
  // Store stable reference to request function
  const requestRef = useRef(request);
  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  /**
   * List all notification templates with optional filters
   */
  const listTemplates = useCallback(async (filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.category) queryParams.set("category", filters.category);
    if (filters.isActive !== undefined) queryParams.set("isActive", filters.isActive);
    if (filters.search) queryParams.set("search", filters.search);
    if (filters.limit) queryParams.set("limit", filters.limit);
    
    const url = queryParams.toString() 
      ? `${endpoints.notificationTemplates.base}?${queryParams.toString()}`
      : endpoints.notificationTemplates.base;
      
    const data = await requestRef.current(url);
    return data;
  }, []);

  /**
   * Get a single notification template by ID
   */
  const getTemplate = useCallback(async (id) => {
    const data = await requestRef.current(endpoints.notificationTemplates.byId(id));
    return data.template;
  }, []);

  /**
   * Create a new notification template
   */
  const createTemplate = useCallback(async (templateData) => {
    const data = await requestRef.current(endpoints.notificationTemplates.base, {
      method: "POST",
      body: templateData,
    });
    return data;
  }, []);

  /**
   * Update an existing notification template
   */
  const updateTemplate = useCallback(async (id, updates) => {
    const data = await requestRef.current(endpoints.notificationTemplates.byId(id), {
      method: "PUT",
      body: updates,
    });
    return data;
  }, []);

  /**
   * Delete a notification template
   */
  const deleteTemplate = useCallback(async (id) => {
    const data = await requestRef.current(endpoints.notificationTemplates.byId(id), {
      method: "DELETE",
    });
    return data;
  }, []);

  /**
   * Duplicate a notification template
   */
  const duplicateTemplate = useCallback(async (id, newKey, newName) => {
    const data = await requestRef.current(endpoints.notificationTemplates.duplicate(id), {
      method: "POST",
      body: { newKey, newName },
    });
    return data;
  }, []);

  /**
   * Preview a template with sample variables
   */
  const previewTemplate = useCallback(async (id, variables = {}) => {
    const data = await requestRef.current(endpoints.notificationTemplates.preview(id), {
      method: "POST",
      body: { variables },
    });
    return data;
  }, []);

  /**
   * Send a test notification
   */
  const testTemplate = useCallback(async (id, recipient, variables = {}, channels = null) => {
    const data = await requestRef.current(endpoints.notificationTemplates.test(id), {
      method: "POST",
      body: { recipient, variables, channels },
    });
    return data;
  }, []);

  /**
   * Toggle template active state
   */
  const toggleTemplate = useCallback(async (id) => {
    const data = await requestRef.current(endpoints.notificationTemplates.toggle(id), {
      method: "POST",
    });
    return data;
  }, []);

  /**
   * Get template metrics
   */
  const getTemplateMetrics = useCallback(async (id, period = "30d") => {
    const data = await requestRef.current(endpoints.notificationTemplates.metrics(id, period));
    return data;
  }, []);

  /**
   * Get metadata (categories, channels, priorities)
   */
  const getMetadata = useCallback(async () => {
    const data = await requestRef.current(endpoints.notificationTemplates.metadata);
    return data;
  }, []);

  /**
   * Get overall notification statistics
   */
  const getStatsOverview = useCallback(async () => {
    const data = await requestRef.current(endpoints.notificationTemplates.stats);
    return data.stats || data;
  }, []);

  return {
    // CRUD
    listTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    
    // Actions
    previewTemplate,
    testTemplate,
    toggleTemplate,
    
    // Metrics
    getTemplateMetrics,
    getStatsOverview,
    
    // Metadata
    getMetadata,
    
    // State
    loading,
    error,
  };
}
